import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
  DriveFileDto,
  TeacherDocumentResponseDto,
  CreatePodcastRequestDtoCefrLevel,
} from "@packages/api/madlenAPI.schemas";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import {
  getClassroomScopes,
  getListFilesQueryKey,
  googleLogin,
  useDeleteFile,
  useGetDriveFiles,
  useListFiles,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Text, Pagination } from "../../../../../core";
import CourseSelector from "../course-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { FileUploader } from "@core/file-uploader";
import { useAppSelector } from "@packages/store/hooks";
import GenericFileCard, { GenericFileItem } from "../GenericFileCard.tsx";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector.tsx";
import driveLogo from "@packages/assets/googledrive.png";
import { CEFRSelector } from "@core/cefr-selector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

const loginClassroom = async () => {
  try {
    const neededClassroomScopesResponse = await getClassroomScopes();
    const googleLoginRes = await googleLogin({
      scopes: neededClassroomScopesResponse,
      redirectUrl: window.location.href,
    });
    window.location.href = googleLoginRes.redirectUrl;
    return true;
  } catch (e) {
    console.error(e);
  }
};

const FileStep = ({
  assignmentData,
  onChange,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody & {
    cefrLevel?: CreatePodcastRequestDtoCefrLevel;
  };
  onChange: (
    data: Partial<
      CreateHomeworkAssignmentRequestBody & {
        cefrLevel?: CreatePodcastRequestDtoCefrLevel;
      }
    >,
  ) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState(assignmentData.prompt || "");
  const [selectedGrade, setSelectedGrade] = useState(assignmentData.grade || 8);
  const [selectedCourse, setSelectedCourse] = useState("ENGLISH"); // defulat english MLT
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const [curriculumType, setCurriculumType] = useState<CurriculumType>("MEB");
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>("AP");
  const [didGetDriveFileAccess, setDidGetDriveFileAccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<GenericFileItem[]>([]);
  const [uploadMode, setUploadMode] = useState<"drive" | "upload" | null>(
    "upload",
  );
  const [selectedUploadedFiles, setSelectedUploadedFiles] = useState<number[]>(
    [],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8);
  const [uploadedFilesCurrentPage, setUploadedFilesCurrentPage] = useState(1);
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" ||
    userEmail === "veyis@madlen.io" ||
    userEmail === "kadriyezobu@gmail.com";
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState<number>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [driveSearchTerm, setDriveSearchTerm] = useState("");
  const listFilesQueryKey = getListFilesQueryKey();
  const [thumbnailErrorMap, setThumbnailErrorMap] = useState<
    Record<string | number, boolean>
  >({});
  const [uploadedFileSearchTerm, setUploadedFileSearchTerm] = useState("");
  const [selectedCefrLevel, setSelectedCefrLevel] =
    useState<CreatePodcastRequestDtoCefrLevel>(
      (assignmentData.cefrLevel as CreatePodcastRequestDtoCefrLevel) ||
        CreatePodcastRequestDtoCefrLevel.A1,
    );

  const {
    data: uploadedFilesData,
    isLoading: isUploadedFilesLoading,
    isFetching: isUploadedFilesFetching,
  } = useListFiles(
    {
      page: uploadedFilesCurrentPage,
      size: pageSize,
      ...(uploadedFileSearchTerm.trim() && {
        fileName: uploadedFileSearchTerm.trim(),
      }),
    },
    {
      query: {
        refetchOnWindowFocus: false,
      },
    },
  );

  useEffect(() => {
    /*
    const newCourse =
      i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN";
      */
    setSelectedCourse("ENGLISH"); // MLT
    setTopic("");

    onChange({
      courseType: "ENGLISH",
      prompt: "",
    });
  }, [i18n.language]);

  const deleteFile = useDeleteFile({
    mutation: {
      onMutate: (data) => {
        setIsDeleting(data.id);
      },
      onSettled: () => {
        setIsDeleting(undefined);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListFilesQueryKey({
            page: uploadedFilesCurrentPage,
            size: pageSize,
            ...(uploadedFileSearchTerm.trim() && {
              fileName: uploadedFileSearchTerm.trim(),
            }),
          }),
        });
      },
    },
  });

  const {
    data: driveFilesData,
    error: driveFilesError,
    refetch: refetchDriveFiles,
    isLoading: isDriveFilesLoading,
    isFetching: isDriveFilesFetching,
  } = useGetDriveFiles(
    {
      page: currentPage,
      size: pageSize,
      ...(driveSearchTerm.trim() && { searchQuery: driveSearchTerm.trim() }),
    },
    {
      query: {
        enabled: didGetDriveFileAccess,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  useEffect(() => {
    if (
      driveFilesError?.response?.status === 401 ||
      driveFilesError?.response?.status === 404
    ) {
      loginClassroom();
    }
  }, [driveFilesError, refetchDriveFiles]);

  const handleDriveSelect = async () => {
    setUploadMode("drive");
    setDidGetDriveFileAccess(true);
    setCurrentPage(1);
    setSelectedFiles([]);
    onChange({
      generator: "DRIVE_FILE",
      driveFileIds: [],
      teacherDocumentIds: [],
    });
  };

  const handleUploadSelect = () => {
    setUploadMode("upload");
    setSelectedFiles([]);
    onChange({
      generator: "TEACHER_DOCUMENT",
      driveFileIds: [],
      teacherDocumentIds: [],
    });
  };

  useEffect(() => {
    if (uploadMode === "drive") {
      onChange({ generator: "DRIVE_FILE" });
    } else if (uploadMode === "upload") {
      onChange({ generator: "TEACHER_DOCUMENT" });
    }
  }, [uploadMode]);

  const toggleFileSelection = (file: GenericFileItem) => {
    setSelectedFiles([file]);
    if (uploadMode === "drive") {
      onChange({ driveFileIds: [file.id as string], teacherDocumentIds: [] });
    } else if (uploadMode === "upload") {
      onChange({ teacherDocumentIds: [file.id as number], driveFileIds: [] });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (driveFilesData?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const isLoadingDrive = isDriveFilesLoading || isDriveFilesFetching;
  const isLoadingUploaded = isUploadedFilesLoading || isUploadedFilesFetching;

  const driveFiles = driveFilesData?.data;

  const handleUploadedFileSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUploadedFileSearchTerm(e.target.value);
    setUploadedFilesCurrentPage(1);
  };

  const handleClearUploadedFileSearch = () => {
    setUploadedFileSearchTerm("");
    setUploadedFilesCurrentPage(1);
  };

  const handleUploadedFilesPreviousPage = () => {
    if (uploadedFilesCurrentPage > 1) {
      setUploadedFilesCurrentPage(uploadedFilesCurrentPage - 1);
    }
  };

  const handleUploadedFilesNextPage = () => {
    if (
      uploadedFilesData?.data &&
      uploadedFilesData.page &&
      uploadedFilesData.totalPages &&
      uploadedFilesCurrentPage < uploadedFilesData.totalPages
    ) {
      setUploadedFilesCurrentPage(uploadedFilesCurrentPage + 1);
    }
  };

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t("assignments.generator.properties.grade")}
            </label>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade: number) => {
                setSelectedGrade(grade);
                setSelectedCourse("ENGLISH");
                onChange({
                  grade,
                  courseType: "ENGLISH",
                  topicIds: [],
                });
              }}
            />
          </div>
        </div>
      </div>
      <div>
        <h4 className={`text-sm font-medium text-gray-700 dark:text-gray-200`}>
          {t("assignments.generator.properties.cefrLevel")}
        </h4>
        <CEFRSelector
          selectedLevel={selectedCefrLevel}
          onLevelChange={(level) => {
            setSelectedCefrLevel(level);
            onChange({ cefrLevel: level });
          }}
          className="mt-1"
        />
      </div>
      <div className="space-y-4">
        {/* Curriculum Type Selection 
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={() => setCurriculumType("MEB")}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => setCurriculumType("INTERNATIONAL")}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "INTERNATIONAL"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "INTERNATIONAL"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.internationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>
          */}
        {/* International Curriculum Sub-selection 
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => setInternationalCurriculum(curriculum)}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
          */}
      </div>

      {/*}
      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              selectedCourse={selectedCourse}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                onChange({
                  grade: selectedGrade,
                  courseType: course,
                });
              }}
            />
          </div>
        </div>
      </div>
      */}
      <div className="animate-fadeIn">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {t("assignments.generator.properties.topic")}
        </label>
        <div className="relative">
          <textarea
            data-testid="create-assignment-from-drive-file-topic-input"
            value={topic}
            onChange={(e) => {
              setTopic(e.target.value);
              onChange({ prompt: e.target.value });
            }}
            className="w-full h-20 sm:h-24 p-3 sm:p-4 pr-12 border border-gray-300 dark:border-dark-400 rounded-xl transition-all bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
            placeholder={t("assignments.generator.file.topicPlaceholder")}
          />
          <div className="absolute right-3 bottom-3 text-xs text-gray-400">
            {topic.length}/200
          </div>
        </div>
        <Text
          variant="p"
          className="mt-2 text-sm text-gray-500 dark:text-gray-400"
        >
          assignments.generator.properties.topicDesc
        </Text>
      </div>
      <div className="border border-gray-200 dark:border-dark-400 rounded-xl p-3 sm:p-4 bg-white dark:bg-dark-500">
        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
          <Button
            data-testid="create-assignment-from-drive-file-drive-button"
            rounded="lg"
            bgColor={
              uploadMode === "drive" ? "bg-primary-500 dark:bg-primary-500" : ""
            }
            size="md"
            fullWidth
            className={`border py-3 px-4 ${
              uploadMode === "drive"
                ? "border-primary-500"
                : "border-gray-200 dark:border-dark-300"
            }`}
            onClick={handleDriveSelect}
          >
            <div className="flex items-center justify-center">
              {/* drive image assets from assets folder */}
              <img
                src={driveLogo}
                alt="Drive"
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 p-1 flex-shrink-0"
              />
              <Text
                variant="span"
                status="secondary"
                size="base"
                className="truncate"
              >
                assignments.generator.file.choose
              </Text>
            </div>
          </Button>

          <Button
            data-testid="create-assignment-from-file-button2"
            rounded="lg"
            fullWidth
            size="icon"
            bgColor={
              uploadMode === "upload" ? "bg-blue-50 dark:bg-primary-500" : ""
            }
            className={`border-2 py-3 px-4 ${
              uploadMode === "upload"
                ? "border-blue-500"
                : "border-gray-200 hover:border-primary-300"
            }`}
            onClick={handleUploadSelect}
          >
            <div className="flex items-center justify-center">
              <svg
                className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              <Text
                variant="span"
                status="secondary"
                size="base"
                className="truncate"
              >
                assignments.generator.file.upload
              </Text>
            </div>
          </Button>
        </div>

        {uploadMode === "upload" && (
          <>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
              {t("assignments.generator.file.upload")}
            </label>

            <FileUploader
              onUploadSuccess={(url, response) => {
                queryClient.invalidateQueries({ queryKey: listFilesQueryKey });
                if (response) {
                  const newFileItem: GenericFileItem = {
                    id: response.id!,
                    fileName: response.fileName,
                    viewOrDownloadLink: response.url || response.downloadUrl,
                  };
                  toggleFileSelection(newFileItem);
                }
              }}
              onFileDelete={() => {
                setSelectedFiles([]);
                onChange({ teacherDocumentIds: [] });
              }}
              onUploadError={(error) => {
                setUploadError(error);
              }}
            />
            <Text variant="p" status="error" size="sm">
              {uploadError}
            </Text>

            <div className="mt-4 sm:mt-6 mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={uploadedFileSearchTerm}
                  onChange={(e) => setUploadedFileSearchTerm(e.target.value)}
                  placeholder={t(
                    "assignments.generator.file.searchPlaceholder",
                  )}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-400 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-dark-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {uploadedFileSearchTerm && (
                  <button
                    onClick={() => setUploadedFileSearchTerm("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {isLoadingUploaded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {Array.from({ length: pageSize > 3 ? pageSize : 4 }).map(
                    (_, index) => (
                      <GenericFileCard
                        key={`skeleton-upload-${index}`}
                        isLoading={true}
                      />
                    ),
                  )}
                </div>
              )}

              {!isLoadingUploaded &&
                uploadedFilesData?.data &&
                uploadedFilesData.data.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {uploadedFilesData.data.map(
                      (file: TeacherDocumentResponseDto) => {
                        const fileItem: GenericFileItem = {
                          id: file.id!,
                          fileName: file.fileName,
                          viewOrDownloadLink: file.url || file.downloadUrl,
                        };
                        return (
                          <GenericFileCard
                            key={file.id}
                            file={fileItem}
                            isSelected={selectedFiles.some(
                              (sf) => sf.id === file.id,
                            )}
                            onSelect={() => toggleFileSelection(fileItem)}
                            isBeingDeleted={isDeleting === file.id}
                            onView={() => {
                              if (file.url || file.downloadUrl) {
                                window.open(
                                  file.url || file.downloadUrl!,
                                  "_blank",
                                );
                              }
                            }}
                            onDelete={() =>
                              deleteFile.mutateAsync({ id: file.id! })
                            }
                          />
                        );
                      },
                    )}
                  </div>
                )}
              {!isLoadingUploaded &&
                uploadedFilesData?.data?.length === 0 &&
                uploadedFileSearchTerm && (
                  <div className="text-center py-4">
                    <Text variant="p" status="secondary" size="base">
                      {t("assignments.generator.file.noSearchResults")}
                    </Text>
                  </div>
                )}
              {!isLoadingUploaded &&
                (!uploadedFilesData?.data ||
                  uploadedFilesData.data.length === 0) &&
                !uploadedFileSearchTerm && (
                  <div className="text-center py-4">
                    <Text variant="p" status="secondary" size="base">
                      {t("assignments.generator.file.noFilesUploadedYet")}
                    </Text>
                  </div>
                )}
            </div>
            {(uploadedFilesCurrentPage > 1 ||
              (uploadedFilesData?.data &&
                uploadedFilesData.page &&
                uploadedFilesData.totalPages &&
                uploadedFilesCurrentPage < uploadedFilesData.totalPages) ||
              (isLoadingUploaded && uploadedFilesCurrentPage > 0)) && (
              <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <button
                  onClick={handleUploadedFilesPreviousPage}
                  disabled={uploadedFilesCurrentPage <= 1 || isLoadingUploaded}
                  className="px-3 sm:px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto text-sm sm:text-base min-w-0"
                >
                  {t("common.previous")}
                </button>
                <span className="px-2 sm:px-4 py-2 text-sm text-gray-600 dark:text-gray-400 text-center whitespace-nowrap">
                  {t("common.page")} {uploadedFilesCurrentPage}
                  {isLoadingUploaded
                    ? "..."
                    : uploadedFilesData?.data &&
                        uploadedFilesData.page &&
                        uploadedFilesData.totalPages &&
                        uploadedFilesCurrentPage < uploadedFilesData.totalPages
                      ? ""
                      : t("common.pagination.lastPage")}
                </span>
                <button
                  onClick={handleUploadedFilesNextPage}
                  disabled={
                    isLoadingUploaded ||
                    !(
                      uploadedFilesData?.data &&
                      uploadedFilesData.page &&
                      uploadedFilesData.totalPages &&
                      uploadedFilesCurrentPage < uploadedFilesData.totalPages
                    )
                  }
                  className="px-3 sm:px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto text-sm sm:text-base min-w-0"
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </>
        )}

        {uploadMode === "drive" && (
          <div className="mt-6 sm:mt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2 sm:gap-0">
              <Text variant="h3">assignments.generator.file.files</Text>
            </div>
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  value={driveSearchTerm}
                  onChange={(e) => {
                    setDriveSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder={t(
                    "assignments.generator.file.searchPlaceholder",
                  )}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm sm:text-base"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                {driveSearchTerm && (
                  <button
                    onClick={() => {
                      setDriveSearchTerm("");
                      setCurrentPage(1);
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4">
              {isLoadingDrive &&
                Array.from({ length: pageSize }).map((_, index) => (
                  <GenericFileCard
                    key={`skeleton-drive-${index}`}
                    isLoading={true}
                  />
                ))}
              {!isLoadingDrive &&
                driveFiles?.map((driveFile: DriveFileDto) => {
                  const fileItem: GenericFileItem = {
                    id: driveFile.id!,
                    title: driveFile.title,
                    thumbnailUrl: driveFile.thumbnailUrl,
                    alternateLink: driveFile.alternateLink,
                  };
                  return (
                    <GenericFileCard
                      key={driveFile.id}
                      file={fileItem}
                      isSelected={selectedFiles.some(
                        (sf) => sf.id === driveFile.id,
                      )}
                      onSelect={() => toggleFileSelection(fileItem)}
                      thumbnailError={thumbnailErrorMap[driveFile.id!] || false}
                      onThumbnailError={(fileId: string | number) =>
                        setThumbnailErrorMap((prevMap) => ({
                          ...prevMap,
                          [fileId]: true,
                        }))
                      }
                    />
                  );
                })}
            </div>

            {(currentPage > 1 ||
              driveFilesData?.hasNextPage ||
              (isLoadingDrive && currentPage > 0)) && (
              <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage <= 1 || isLoadingDrive}
                  className="px-3 sm:px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto text-sm sm:text-base min-w-0"
                >
                  {t("common.previous")}
                </button>
                <span className="px-2 sm:px-4 py-2 text-sm text-gray-600 dark:text-gray-400 text-center whitespace-nowrap">
                  {t("common.page")} {currentPage}
                  {isLoadingDrive
                    ? "..."
                    : driveFilesData?.hasNextPage
                      ? ""
                      : t("common.pagination.lastPage")}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={isLoadingDrive || !driveFilesData?.hasNextPage}
                  className="px-3 sm:px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 w-full sm:w-auto text-sm sm:text-base min-w-0"
                >
                  {t("common.next")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FileStep;
